// src/hooks/usePullToRefresh/usePullToRefresh.ts
import { useState, useRef, useEffect } from 'react';

interface UsePullToRefreshProps {
  onRefresh: () => Promise<void>;
  threshold?: number;
  disabled?: boolean;
}

export const usePullToRefresh = ({
  onRefresh,
  threshold = 100, // 🔥 Naikin dari 80 → 100
  disabled = false
}: UsePullToRefreshProps) => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const startY = useRef(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const pulling = useRef(false);
  const passedThreshold = useRef(false);
  const validPull = useRef(false);
  const rafId = useRef<number | null>(null);
  const latestOnRefresh = useRef(onRefresh);

  useEffect(() => {
    latestOnRefresh.current = onRefresh;
  }, [onRefresh]);

  useEffect(() => {
    if (disabled) return;
    const container = containerRef.current;
    if (!container) return;

    const cancelRAF = () => {
      if (rafId.current !== null) {
        cancelAnimationFrame(rafId.current);
        rafId.current = null;
      }
    };

    const setPullDistanceRaf = (value: number) => {
      cancelRAF();
      rafId.current = requestAnimationFrame(() => {
        setPullDistance(value);
        rafId.current = null;
      });
    };

    const resetAll = () => {
      pulling.current = false;
      passedThreshold.current = false;
      validPull.current = false;
      startY.current = 0;
      cancelRAF();
      setPullDistance(0);
    };

    // Container bisa jadi elemen scroll sendiri (layout 100vh) atau ikut scroll window
    const getScrollTop = () => container.scrollTop || window.scrollY;

    const handleTouchStart = (e: TouchEvent) => {
      if (e.touches.length !== 1 || getScrollTop() > 0 || isRefreshing) {
        pulling.current = false;
        return;
      }
      startY.current = e.touches[0].clientY;
      pulling.current = true;
      passedThreshold.current = false;
      validPull.current = false;
      setPullDistance(0);
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!pulling.current || isRefreshing) return;
      if (e.touches.length !== 1) {
        resetAll();
        return;
      }

      const currentY = e.touches[0].clientY;
      const distance = currentY - startY.current;

      if (distance > 0 && getScrollTop() === 0) {
        if (!validPull.current) validPull.current = true;
        e.preventDefault();
        
        // 🔥 Tambah resistance — makin jauh makin berat
        const resistance = 0.4; // 0.4 = 40% dari jarak asli (makin kecil makin berat)
        const resistedDistance = distance * resistance;
        const newDistance = Math.min(resistedDistance, threshold * 1.5);
        
        setPullDistanceRaf(newDistance);
        
        // Update threshold flag based on CURRENT distance
        if (newDistance >= threshold) {
          passedThreshold.current = true;
        } else {
          // 🔥 Jika balik ke bawah threshold → reset flag
          passedThreshold.current = false;
        }
      } else if (distance < 0 && validPull.current) {
        // User tarik ke atas → cancel
        resetAll();
      } else if (!validPull.current && distance <= 0) {
        resetAll();
      }
    };

    const handleTouchEnd = async () => {
      if (!pulling.current || !validPull.current) {
        resetAll();
        return;
      }
      pulling.current = false;

      if (passedThreshold.current && !isRefreshing) {
        setIsRefreshing(true);
        try {
          await latestOnRefresh.current();
        } finally {
          setIsRefreshing(false);
        }
      }

      resetAll();
    };

    const handleTouchCancel = () => {
      resetAll();
    };

    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd, { passive: true });
    container.addEventListener('touchcancel', handleTouchCancel);

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
      container.removeEventListener('touchcancel', handleTouchCancel);
      cancelRAF();
    };
  }, [threshold, isRefreshing, disabled]);

  return {
    containerRef,
    isRefreshing,
    pullDistance,
    threshold
  };
};